import { useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Form, Input, Select } from "antd";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks/redux";

import Dropdown from "../../../components/Dropdown";
import { sortOptionsAlphabetically } from "../../../utils/helpers/sortOptionsAlphabetically";
import StyledModal from "../../../components/CustomModal";
import { fetchDeleteReasons } from "../../../redux/features/deleteReason/deleteReasonAsyncThunk";
import { FormProps } from "antd/lib";
import { StyledButton } from "../../../components/styles";
import { deleteLostItem } from "../../../redux/features/report/reportAsyncThunk";
import { RequestResults } from "../../../redux/common/RequestStatus";
import { convertIntoBoardTicket, updateTicketStatusLocally } from "../../../redux/features/board/columnsSlice";
import { useNavigate } from "react-router-dom";
import SystemPayRoutes from "../../../utils/constants/Routes";
import { ReasonFooterContainer } from "./styles";
import { Report } from "../../../types";
import { updateLostItemStatus } from "../../../redux/features/board/columnsAsyncThunk";
import { clearReport } from "../../../redux/features/report/reportSlice";
import { toast } from "react-toastify";
import ConfirmationModal from "../../../components/ConfirmationModal";
import { NotFoundReason } from "../../../types/ReasonTypes";
import { LostStatuses } from "../../../types/LostStatuses";
import { FooterButtons } from "../../../components/ConfirmationModal/styles";

interface DeleteWithReasonProps {
  isOpen: boolean;
  onClose: () => void;
  selectedRecord: Report | null;
}

const DeleteWithReason = ({ isOpen, onClose, selectedRecord }: DeleteWithReasonProps) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [showEmailSendConfirm, setShowEmailSendConfirm] = useState(false);
  const [form] = Form.useForm();
  const { data: deleteReasonData, status } = useAppSelector((state) => state.deleteReason);
  const navigate = useNavigate();

  useEffect(() => {
    if (status === RequestResults.idle) {
      dispatch(fetchDeleteReasons());
    }
  }, [dispatch, status]);
  const selectedDeleteReason = Form.useWatch("discardReason", form);

  const onFinish = async (sendEmail = true) => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      const requestPayload = {
        id: selectedRecord?.id?.toString() ?? "",
        status: LostStatuses.Deleted,
        reason: values?.discardReason?.toLocaleLowerCase() === "other" ? values?.discardReasonText : values?.discardReason,
        sendEmail
      };
      await dispatch(updateLostItemStatus(requestPayload)).unwrap();
      toast.success("Report deleted successfully");
      dispatch(updateTicketStatusLocally({ ticket: convertIntoBoardTicket(selectedRecord), sourceStatus: selectedRecord?.status ?? 0, targetStatus: LostStatuses.Deleted }));
      dispatch(clearReport());
      form.resetFields();
      navigate(SystemPayRoutes.reports);
      onClose();
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = async () => {
    try {
      const values = await form.validateFields();
      if ((values?.discardReason?.toLowerCase().includes(NotFoundReason.toLowerCase()))) {
        setShowEmailSendConfirm(true);
      }
      else {
        onFinish(false);
      }
    } catch (error) {
    }
  }

  const deleteReasonOptions = useMemo(() => {
    return deleteReasonData?.map((deleteReason) => ({
      label: deleteReason.reason ?? "",
      value: deleteReason.reason ?? "",
      key: deleteReason.id?.toString() ?? "",
    }));
  }, [deleteReasonData]);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  return (
    <StyledModal
      draggable={true}
      footer={
        <ReasonFooterContainer>
          <StyledButton onClick={handleClose} disabled={loading}>
            Cancel
          </StyledButton>
          <StyledButton type="primary" onClick={() => handleFinish()} loading={loading} disabled={loading}>
            Delete
          </StyledButton>
        </ReasonFooterContainer>
      }
      open={isOpen}
      onCancel={() => onClose()}
      title="Are you sure you want delete?"
    >
      <Form form={form} onFinish={onFinish} autoComplete="off" layout="vertical" style={{ marginTop: "20px" }}>
        <Form.Item name="discardReason" label="Delete Reason" rules={[{ required: true, message: "Please select reason" }]}>
          <Select placeholder={"Select Reason for delete"} options={sortOptionsAlphabetically(deleteReasonOptions, "label")} />
        </Form.Item>

        {selectedDeleteReason?.toLowerCase() === "other" && (
          <Form.Item name="discardReasonText" label="Reason" rules={[{ required: true, message: "Please enter reason" }]}>
            <Input.TextArea placeholder="Reason" name="discardReasonText" />
          </Form.Item>
        )}

        <Form.Item
          name="delete"
          valuePropName="checked"
          rules={[
            {
              validator(_, value) {
                if (value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error("Please check the box to confirm delete."));
              },
            },
          ]}
        >
          <Checkbox id="delete">
            I confirm that I want to delete
          </Checkbox>
        </Form.Item>
      </Form>

      {showEmailSendConfirm && (
        <ConfirmationModal
          isModalOpen={showEmailSendConfirm}
          onCancel={() => setShowEmailSendConfirm(false)}
          message={"Do you want to send the guest an Delete Email?"}
          title={"Send Email"}
          loading={loading}
          handleSave={() => onFinish(true)}
          CancelButtonText="No"
          confirmButtonText="Yes"
          footerButton={
            <FooterButtons>
              <Button onClick={() => onFinish(false)} disabled={loading}>{"No"}</Button>
              <Button type="primary" onClick={() => onFinish(true)} loading={loading}>
                {"Yes"}
              </Button>
            </FooterButtons>
          }
        />
      )}

    </StyledModal>
  );
};

export default DeleteWithReason;
